import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { COLORS } from '../../../../constants/booking';
import { BOOKING_STATUS } from '../../../../constants/booking';
import { Booking } from '../../../../services/api';

interface BookingStats {
  total: number;
  open: number;
  inProgress: number;
  completed: number;
  cancelled: number;
}

interface BookingSummaryCardsProps {
  bookings: Booking[];
  onBookingFilterChange?: (statusFilter: string | undefined, cardId: string | null) => void;
  selectedBookingCard?: string | null;
}

const BookingSummaryCards: React.FC<BookingSummaryCardsProps> = ({ bookings, onBookingFilterChange, selectedBookingCard }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Viewport-based layout: wrap cards ONLY on very small screens (<= 430px)
  const [isVeryNarrow, setIsVeryNarrow] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsVeryNarrow(window.innerWidth <= 430);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate booking statistics
  const bookingStats: BookingStats = React.useMemo(() => {
    const total = bookings.length;
    const open = bookings.filter((b) => 
      b.spec.status === BOOKING_STATUS.PENDING || b.spec.status === BOOKING_STATUS.ASSIGNED
    ).length;
    const inProgress = bookings.filter((b) => 
      b.spec.status === BOOKING_STATUS.IN_TRANSIT || b.spec.status === BOOKING_STATUS.PATIENT_PICKED || b.spec.status === BOOKING_STATUS.PATIENT_DROPPED || (b.spec.status as string) === 'arrived' // legacy
    ).length;
    const completed = bookings.filter((b) => b.spec.status === BOOKING_STATUS.COMPLETED).length;
    const cancelled = bookings.filter((b) => b.spec.status === BOOKING_STATUS.CANCELLED).length;

    return {
      total,
      open,
      inProgress,
      completed,
      cancelled,
    };
  }, [bookings]);

  // Helper function to create donut chart SVG
  const createDonutChart = (percentage: number, color: string, size: number = 40) => {
    const strokeWidth = 8;
    const radius = size / 2 - strokeWidth / 2 - 1;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isDark ? '#334155' : '#e2e8f0'}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    );
  };

  // Handle scroll to bookings section
  const handleScrollToBookings = () => {
    const bookingsSection = document.getElementById('bookings-section');
    if (bookingsSection) {
      bookingsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle card click with filter and scroll
  const handleCardClick = (statusFilter: string | undefined, cardId: string) => {
    if (onBookingFilterChange) {
      onBookingFilterChange(statusFilter, cardId);
    }
    handleScrollToBookings();
  };

  // Status card data
  const statusCards = [
    {
      id: 'total',
      label: 'Total Request',
      value: bookingStats.total,
      color: '#3b82f6',
      percentage: 100,
      isTotal: true,
      onClick: () => handleCardClick(undefined, 'total'), // Clear filter for total
    },
    {
      id: 'open',
      label: 'Open',
      value: bookingStats.open,
      color: '#f59e0b',
      percentage: bookingStats.total > 0 ? (bookingStats.open / bookingStats.total) * 100 : 0,
      onClick: () => handleCardClick('pending,assigned', 'open'), // Show pending and assigned
    },
    {
      id: 'in_progress',
      label: 'In Progress',
      value: bookingStats.inProgress,
      color: '#64748b',
      percentage: bookingStats.total > 0 ? (bookingStats.inProgress / bookingStats.total) * 100 : 0,
      onClick: () => handleCardClick('in_transit,patient_picked,patient_dropped', 'in_progress'), // Show in_transit, patient_picked, patient_dropped
    },
    {
      id: 'completed',
      label: 'Completed',
      value: bookingStats.completed,
      color: '#10b981',
      percentage: bookingStats.total > 0 ? (bookingStats.completed / bookingStats.total) * 100 : 0,
      onClick: () => handleCardClick('completed', 'completed'), // Show completed
    },
    {
      id: 'cancelled',
      label: 'Cancelled',
      value: bookingStats.cancelled,
      color: '#ef4444',
      percentage: bookingStats.total > 0 ? (bookingStats.cancelled / bookingStats.total) * 100 : 0,
      onClick: () => handleCardClick('cancelled', 'cancelled'), // Show cancelled
    },
  ];

  return (
    <div
      className="summary-cards-container"
      style={{
        display: 'flex',
        flexDirection: isVeryNarrow ? 'column' : 'row',
        gap: '8px',
        flexWrap: 'wrap',
        marginTop: '16px',
        overflow: 'visible',
      }}
    >
      {statusCards.map((card) => {
        const isSelected = selectedBookingCard === card.id;
        return (
        <div
          key={card.label}
          className="summary-card"
          onClick={card.onClick}
          style={{
            flex: isVeryNarrow ? '0 0 auto' : '1 1 0',
            width: isVeryNarrow ? '100%' : undefined,
            minWidth: isVeryNarrow ? undefined : 0,
            backgroundColor: isDark ? COLORS.THEME.DARK.BACKGROUND : COLORS.THEME.LIGHT.BACKGROUND,
            borderRadius: '6px',
            padding: '8px 0px',
            border: `2px solid ${isSelected ? card.color : (isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER)}`,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            transition: 'border-color 0.2s, transform 0.1s',
            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = card.color;
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.borderColor = isDark ? COLORS.THEME.DARK.BORDER : COLORS.THEME.LIGHT.BORDER;
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {/* Part 1: Pie Chart - 20% */}
          <div style={{ width: '20%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {createDonutChart(card.percentage, card.color, 36)}
          </div>
          
          {/* Part 2: Text - 60% */}
          <div style={{ width: '40%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div
              style={{
                fontSize: 'clamp(11px, 1.1vw, 14px)',
                fontWeight: '600',
                color: card.color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {card.label}
            </div>
          </div>
          
          {/* Part 3: Number - 20% */}
          <div style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div
              style={{
                fontSize: 'clamp(18px, 2vw, 26px)',
                fontWeight: '700',
                color: isDark ? COLORS.THEME.DARK.TEXT_PRIMARY : COLORS.THEME.LIGHT.TEXT_PRIMARY,
                lineHeight: '1',
              }}
            >
              {card.value}
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default BookingSummaryCards;

